// src/components/ImageGenerator.jsx
import React, { useState } from 'react';
import { Download, RefreshCw, Image as ImageIcon, AlertCircle, CheckCircle, Wand2 } from 'lucide-react';
import { withBackend } from '../utils/api';

const ImageGenerator = ({ imagesuggestion, apiKey, onImageGenerated, visualSuggestionDescription }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);
  const [customPrompt, setCustomPrompt] = useState(imagesuggestion?.prompt || '');
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  const generateImage = async (prompt) => {
    if (!apiKey) {
      setError('OpenAI API key is required');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(withBackend('/generate-image'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: apiKey,
          prompt,
          size: '1024x1024',
          quality: 'standard',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Image generation failed (${response.status})`);
      }

      const result = await response.json();
      const imageUrl = result.image_url || result.imageUrl;

      if (!imageUrl) {
        throw new Error('No image URL returned from generator');
      }

      const proxiedUrl = withBackend(`/proxy-image?url=${encodeURIComponent(imageUrl)}`);
      const finalUrl = proxiedUrl || imageUrl;

      setGeneratedImage({
        url: finalUrl,
        originalUrl: imageUrl,
        prompt: result.prompt_used || prompt,
        revisedPrompt: result.revised_prompt || null,
        size: result.size || '1024x1024',
        quality: result.quality || 'standard',
      });

      if (onImageGenerated) {
        onImageGenerated(result);
      }

    } catch (err) {
      console.error('Image generation error:', err);
      setError(err.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = () => {
    generateImage(customPrompt);
  };

  const handleRegenerate = () => {
    generateImage(customPrompt);
  };

  const handleAutoFill = () => {
    if (visualSuggestionDescription) {
      setCustomPrompt(visualSuggestionDescription);
    }
  };

  const downloadImage = async () => {
    if (!generatedImage?.url) return;

    try {
      // Use the proxy URL for download to avoid CORS issues
      const downloadUrl = generatedImage.url;
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generated-image-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download image. Please try right-clicking the image and selecting "Save image as..."');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-blue-50 rounded-lg">
          <ImageIcon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Visual Suggestion
          </h3>
          {imagesuggestion && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Purpose:</strong> {imagesuggestion.purpose}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Placement:</strong> {imagesuggestion.placement}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Priority:</strong> 
                <span className={`ml-2 px-2 py-1 rounded text-xs ${
                  imagesuggestion.priority === 'high' ? 'bg-red-100 text-red-800' :
                  imagesuggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {imagesuggestion.priority}
                </span>
              </p>
              <p className="text-sm text-gray-600">
                <strong>Description:</strong> {imagesuggestion.description}
              </p>
            </div>
          )}

          {/* Custom Prompt Input */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Image Prompt
              </label>
              {visualSuggestionDescription && (
                <button
                  onClick={handleAutoFill}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                  title="Auto-fill from visual suggestion"
                >
                  <Wand2 className="w-3 h-3" />
                  Auto-fill
                </button>
              )}
            </div>
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              disabled={isGenerating}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mb-4">
            {!generatedImage ? (
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !customPrompt.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <ImageIcon className="w-4 h-4" />
                )}
                {isGenerating ? 'Generating...' : 'Generate Image'}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleRegenerate}
                  disabled={isGenerating || !customPrompt.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isGenerating ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  {isGenerating ? 'Regenerating...' : 'Regenerate'}
                </button>
                <button
                  onClick={downloadImage}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            )}
          </div>

          {/* Generated Image Display */}
          {generatedImage && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-green-50 border-b border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-green-700 font-medium">
                    Image generated successfully
                  </span>
                </div>
                <button
                  onClick={() => {
                    // Use the proxy URL for download to avoid CORS issues
                    const link = document.createElement('a');
                    link.href = generatedImage.url;
                    link.download = `generated-image-${Date.now()}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
              <div className="p-6">
                {/* Clickable image display */}
                <div className="mb-4">
                  <div 
                    className="relative cursor-pointer group"
                    onClick={() => {
                      console.log('Image clicked, expanding...');
                      setIsImageExpanded(true);
                    }}
                  >
                    <img
                      src={generatedImage.url}
                      alt={generatedImage.prompt}
                      className="w-full max-w-2xl mx-auto rounded-lg shadow-lg transition-transform group-hover:scale-105"
                      style={{ minHeight: '300px', maxHeight: '500px', objectFit: 'contain' }}
                      onError={(e) => {
                        // Try the original URL as fallback
                        if (generatedImage.originalUrl && e.target.src !== generatedImage.originalUrl) {
                          e.target.src = generatedImage.originalUrl;
                        } else {
                          setError('Failed to load generated image');
                        }
                      }}
                    />
                    {/* Click to expand overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2">
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">Click image to expand</p>
                </div>
                
                {/* Prompt details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Generation Details</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Original prompt:</span>
                      <p className="mt-1 text-gray-700">{generatedImage.prompt}</p>
                    </div>
                    {generatedImage.revisedPrompt && generatedImage.revisedPrompt !== generatedImage.prompt && (
                      <div>
                        <span className="font-medium">AI-revised prompt:</span>
                        <p className="mt-1 text-gray-700">{generatedImage.revisedPrompt}</p>
                      </div>
                    )}
                    <div className="flex gap-4 text-xs text-gray-500 pt-2 border-t border-gray-200">
                      <span><strong>Size:</strong> {generatedImage.size}</span>
                      <span><strong>Quality:</strong> {generatedImage.quality}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Expansion Modal */}
      {isImageExpanded && generatedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4" 
          style={{zIndex: 9999}}
          onClick={(e) => {
            e.stopPropagation();
            setIsImageExpanded(false);
          }}
        >
          <div 
            className="relative max-w-7xl max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setIsImageExpanded(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Full-size image */}
            <img
              src={generatedImage.url}
              alt={generatedImage.prompt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                // Try the original URL as fallback
                if (generatedImage.originalUrl && e.target.src !== generatedImage.originalUrl) {
                  e.target.src = generatedImage.originalUrl;
                } else {
                  setError('Failed to load expanded image');
                  setIsImageExpanded(false);
                }
              }}
            />
            
            {/* Download button in modal */}
            <div className="absolute bottom-4 right-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadImage();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-90 text-gray-800 rounded-lg hover:bg-opacity-100 transition-all shadow-lg"
              >
                <Download className="w-4 h-4" />
                Download Full Size
              </button>
            </div>
          </div>
          
          {/* Click outside to close */}
          <div 
            className="absolute inset-0 -z-10"
            onClick={() => setIsImageExpanded(false)}
          />
        </div>
      )}
    </div>
  );
};

export default ImageGenerator;